import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ButtonGroup, Button } from '@nextui-org/react';

import { electricityPricesData } from '../api/electricityData';

const ElectricityCharts = () => {
  const [rawData] = useState(electricityPricesData);
  const [timeFrame, setTimeFrame] = useState(12); // 12 months or 60 months
  const [displayType, setDisplayType] = useState('nominal'); // 'nominal' or 'indexed'

  // Define region names for tooltip
  const regionNames = {
    CT: "Connecticut",
    US: "United States",
    NEW: "New England"
  };

  // Define colors for different regions
  const regionColors = {
    CT: "#1f77b4",  // blue
    US: "#2ca02c",  // green
    NEW: "#ff7f0e"  // orange
  };

  // Transform and sort the data
  const processedData = useMemo(() => {
    // Group by month
    const groupedByMonth = rawData.reduce((acc, item) => {
      const { month, region, price } = item;
      if (!acc[month]) {
        acc[month] = { month };
      }
      acc[month][region] = parseFloat(price);
      return acc;
    }, {});

    // Convert to array and sort by month
    const sortedData = Object.values(groupedByMonth)
      .sort((a, b) => new Date(a.month) - new Date(b.month));

    // Filter by time frame (get last N months)
    const filteredData = sortedData.slice(-timeFrame);

    // If indexed, calculate indexed values
    if (displayType === 'indexed' && filteredData.length > 0) {
      const baseData = filteredData[0];
      return filteredData.map(item => {
        const indexedItem = { month: item.month };
        Object.keys(item).forEach(key => {
          if (key !== 'month' && baseData[key]) {
            indexedItem[key] = Number(((item[key] / baseData[key]) * 100).toFixed(1));
          }
        });
        return indexedItem;
      });
    }

    return filteredData;
  }, [rawData, timeFrame, displayType]);

  // Get all regions sorted by their values
  const sortedRegions = useMemo(() => {
    const regions = ['CT', 'US', 'NEW'];

    // Get the most recent month's data
    const latestMonth = processedData[processedData.length - 1];

    // Sort regions by their values in descending order
    return regions.sort((a, b) => {
      const valueA = latestMonth?.[a] || 0;
      const valueB = latestMonth?.[b] || 0;
      return valueB - valueA;
    });
  }, [processedData]);

  // Add parseDate function
  const parseDate = (dateString) => {
    const [year, month] = dateString.split('-');
    const date = new Date(year, month - 2);
    // Add one month to correct the display
    date.setMonth(date.getMonth() + 1);
    return date;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    // Sort payload by value in descending order
    const sortedPayload = [...payload].sort((a, b) => b.value - a.value);

    return (
      <div className="bg-white p-4 border rounded shadow">
        <p className="font-semibold mb-2">
          {parseDate(label).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </p>
        {sortedPayload.map((entry, index) => (
          <div
            key={index}
            className="flex items-center gap-2 text-sm"
            style={{ color: entry.color }}
          >
            <div className="font-medium">
              {regionNames[entry.name]}:
            </div>
            <div>
              {displayType === 'indexed'
                ? `${entry.value?.toFixed(1)}`
                : `${entry.value?.toFixed(2)}¢`}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">
        Electricity Prices {displayType === 'indexed' ? '(Indexed, Base 100)' : '(cents per kWh)'}
      </h3>

      {/* Control Panel */}
      <div className="flex flex-col sm:flex-row gap-4 mb-4 items-center justify-center">
        {/* Time Frame Selector */}
        <div className="flex flex-col items-center">
          <span className="text-sm font-medium mb-2">Time Frame</span>
          <ButtonGroup>
            <Button
              style={{
                backgroundColor: timeFrame === 12 ? '#245BA9' : 'transparent',
                borderColor: '#245BA9',
                color: timeFrame === 12 ? 'white' : '#245BA9'
              }}
              onClick={() => setTimeFrame(12)}
            >
              1 Year
            </Button>
            <Button
              style={{
                backgroundColor: timeFrame === 60 ? '#245BA9' : 'transparent',
                borderColor: '#245BA9',
                color: timeFrame === 60 ? 'white' : '#245BA9'
              }}
              onClick={() => setTimeFrame(60)}
            >
              5 Years
            </Button>
          </ButtonGroup>
        </div>

        {/* Display Type Selector */}
        <div className="flex flex-col items-center">
          <span className="text-sm font-medium mb-2">Display</span>
          <ButtonGroup>
            <Button
              style={{
                backgroundColor: displayType === 'nominal' ? '#245BA9' : 'transparent',
                borderColor: '#245BA9',
                color: displayType === 'nominal' ? 'white' : '#245BA9'
              }}
              onClick={() => setDisplayType('nominal')}
            >
              Nominal
            </Button>
            <Button
              style={{
                backgroundColor: displayType === 'indexed' ? '#245BA9' : 'transparent',
                borderColor: '#245BA9',
                color: displayType === 'indexed' ? 'white' : '#245BA9'
              }}
              onClick={() => setDisplayType('indexed')}
            >
              Indexed
            </Button>
          </ButtonGroup>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={processedData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="month"
            tickFormatter={(tick) => {
              const date = parseDate(tick);
              return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
            }}
          />
          <YAxis />
          <Tooltip content={<CustomTooltip />} />
          <Legend formatter={(value) => regionNames[value]} />
          {sortedRegions.map((region) => (
            <Line
              key={region}
              type="monotone"
              dataKey={region}
              stroke={regionColors[region]}
              name={region}
              dot={false}
              strokeWidth={region === 'CT' ? 2.5 : 1.5}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ElectricityCharts;
